/**
 * Replays persisted offline queue items after reconnect.
 * Tokens are taken from the current session at replay time (not stored in the queue).
 */
import { appendBehaviorLogRow } from '../services/behaviorLogSheets';
import { appendContactLog } from '../services/contactLogSheets';
import {
  firebaseSignInWithGoogleAccessToken,
  getFirebaseConfig,
  loadFirebaseSession,
  sendMessage,
  upsertThread,
  type FirebaseSession,
} from '../services/firebaseThreadsRest';
import { appendCommunicateArchive } from './communicateArchive';
import type { OfflineQueuedAction } from './offlineActionQueue';
import {
  OFFLINE_ACTION_COMMUNICATE_CONTACT_APPEND,
  OFFLINE_ACTION_COMMUNICATE_SERVER_EMAIL,
  OFFLINE_ACTION_COMMUNICATE_SERVER_SMS,
  OFFLINE_ACTION_COMMUNICATE_THREAD_SEND,
  OFFLINE_ACTION_TEACH_BEHAVIOR_SHEET_APPEND,
} from './offlineActionTypes';
import { postServerEmail, postServerSms } from './serverNotify';

export type ReplayOfflineContext = {
  accessToken: string | null;
};

async function resolveFirebaseSessionForReplay(
  ctx: ReplayOfflineContext
): Promise<FirebaseSession | null> {
  const { apiKey, projectId } = getFirebaseConfig();
  if (!apiKey || !projectId) return null;
  const now = Date.now();
  const stored = loadFirebaseSession();
  if (stored && stored.expiresAtMs > now + 10_000) return stored;
  if (ctx.accessToken?.trim()) {
    try {
      return await firebaseSignInWithGoogleAccessToken(ctx.accessToken.trim());
    } catch {
      return null;
    }
  }
  return null;
}

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

export async function replayOfflineAction(
  action: OfflineQueuedAction,
  ctx: ReplayOfflineContext
): Promise<boolean> {
  const p = asRecord(action.payload);

  try {
    switch (action.type) {
      case OFFLINE_ACTION_COMMUNICATE_SERVER_EMAIL: {
        const to = String(p.to || '').trim();
        const subject = String(p.subject || '');
        const text = String(p.text || '');
        if (!to || !text) return false;
        const res = await postServerEmail({ to, subject, text });
        if (!res.ok) return false;
        appendCommunicateArchive({
          kind: 'email',
          title: to,
          detail: text.slice(0, 160),
        });
        return true;
      }
      case OFFLINE_ACTION_COMMUNICATE_SERVER_SMS: {
        const to = String(p.to || '').trim();
        const body = String(p.body || '');
        if (!to || !body) return false;
        const res = await postServerSms({ to, body });
        if (!res.ok) return false;
        appendCommunicateArchive({
          kind: 'sms',
          title: to,
          detail: body.slice(0, 160),
        });
        return true;
      }
      case OFFLINE_ACTION_COMMUNICATE_CONTACT_APPEND: {
        if (!ctx.accessToken?.trim()) return false;
        const sheetId = String(p.sheetId || '').trim();
        const row = asRecord(p.row);
        if (!sheetId) return false;
        await appendContactLog(ctx.accessToken, sheetId, {
          student: String(row.student ?? ''),
          parent: String(row.parent ?? ''),
          category: String(row.category ?? ''),
          title: String(row.title ?? ''),
          note: String(row.note ?? ''),
          fullMessage: String(row.fullMessage ?? ''),
          school: String(row.school ?? ''),
          subject: String(row.subject ?? ''),
        });
        appendCommunicateArchive({
          kind: 'sheet',
          title: String(row.student ?? '—'),
          detail: String(row.title ?? ''),
        });
        return true;
      }
      case OFFLINE_ACTION_COMMUNICATE_THREAD_SEND: {
        const studentName = String(p.studentName || '').trim();
        const englishText = String(p.englishText || '').trim();
        if (!studentName || !englishText) return false;
        const session = await resolveFirebaseSessionForReplay(ctx);
        if (!session) return false;
        const nowIso = new Date().toISOString();
        const courseId = String(p.courseId || '');
        const translatedText = String(p.translatedText || '');
        const senderName = String(p.senderName || 'Teacher');
        const threadId = await upsertThread(session, {
          studentName,
          courseId,
          updatedAt: nowIso,
          lastMessageText: englishText.slice(0, 140),
          lastMessageAt: nowIso,
        });
        await sendMessage(session, threadId, {
          text: englishText,
          language: 'en',
          translatedText,
          senderName,
          createdAt: nowIso,
        });
        await upsertThread(session, {
          id: threadId,
          studentName,
          courseId,
          updatedAt: nowIso,
          lastMessageText: englishText.slice(0, 140),
          lastMessageAt: nowIso,
        });
        appendCommunicateArchive({
          kind: 'thread',
          title: studentName,
          detail: englishText.slice(0, 160),
        });
        return true;
      }
      case OFFLINE_ACTION_TEACH_BEHAVIOR_SHEET_APPEND: {
        if (!ctx.accessToken?.trim()) return false;
        const spreadsheetId = String(p.spreadsheetId || '').trim();
        const row = asRecord(p.row);
        if (!spreadsheetId) return false;
        await appendBehaviorLogRow(ctx.accessToken, spreadsheetId, {
          courseName: String(row.courseName ?? ''),
          assignmentTitle: String(row.assignmentTitle ?? ''),
          studentName: String(row.studentName ?? ''),
          sentiment: String(row.sentiment ?? ''),
        });
        return true;
      }
      default:
        return true;
    }
  } catch {
    return false;
  }
}
